import { SvgIconComponent } from '@mui/icons-material'
import { ReactNode, forwardRef } from 'react'
import { IconProps } from '@mui/material'
import { LoadedTooltip } from '../display'

/**
 * A text input field with an inline icon to the right of the input content.
 * This is compatible with MUI TextField components and can be used as a
 * custom input component, ensuring normal MUI logic continues to work.
 */
export const InputWithInlineIcon = forwardRef<
  HTMLInputElement,
  InputWithInlineIconProps & { inputRef: React.Ref<HTMLInputElement> }
>(function InputWithInlineIcon(props) {
  const { inputRef, message, Icon, iconProps, tabIndex, ...muiProps } = props

  return (
    <>
      <input ref={inputRef} tabIndex={tabIndex} {...muiProps} />
      {Icon && (
        <LoadedTooltip
          message={message}
          Icon={Icon}
          iconProps={{
            ...iconProps,
            sx: {
              color: (theme) => theme.palette.grey[600],
              fontSize: '1.7rem',
              marginLeft: '0.3lh',
              // Pull to the right the same amount as the icon width including
              // left margin, this ensure that an input in a centered column
              // that has an icon will not be off-center compared to any inputs
              // that don't have an icon.
              marginRight: 'calc((1rem + 0.3lh) * -1)',

              // support overrides of the base styles
              ...iconProps?.sx,
            },
          }}
        />
      )}
    </>
  )
})

export type InlineInputIcon = {
  show: () => boolean
  Icon: SvgIconComponent
  iconProps?: IconProps
  tooltipMessage: ReactNode
  tabIndex?: number
}

type InputWithInlineIconProps = {
  message: ReactNode
  Icon: SvgIconComponent
  iconProps?: IconProps
  tabIndex?: number
}
